import * as React from "react";

import { UpdateControl, UpdateHeadered, CSNclViewMetadata, TBehaviorTypeByDevice } from "../common/communication.base";
import { withContext, K2ComponentState, WithContextPlacementProps, StyleHelper, AcquireControl } from "./k2hoc";
import {
  NclFloaterView,
  NclViewBase,
  NclFloaterAccessor,
  NclMenuView,
  NclMenu,
  NclInplaceView,
  NclVirtualKeyboardDialog,
  NclCodeReaderDialog,
  NclOpenDialog,
  NclSilentOpenDialogContent,
} from "../common/components.ncl";
import { K2FlowPanel } from "./K2FlowPanel";
import { K2Header } from "./K2Expander";
import { SimpleModalWindow, ModalMenuWindow, ModalWindow, ModalWindowBase } from "./K2Modal";
import { K2Menu } from "./K2Menu";
import { ViewRealizerManager } from "../viewrealizer";
import { Context } from "../appcontext";
import { K2OpenDialogContent } from "./K2SilentOpenDialogContnet";
import { K2VirtualKeyboard } from "./K2VirtualKeyboard";
import { K2CodeReader } from "./K2CodeReader";
import { K2OpenDialog } from "./K2OpenDialog";

interface ViewProps extends WithContextPlacementProps {
  overlayBck: boolean;
}

class _View extends React.Component<ViewProps, K2ComponentState<UpdateHeadered>> {
  static displayName = `K2View`;
  private control: NclViewBase<CSNclViewMetadata, UpdateHeadered>;
  private window: ModalWindowBase;
  private timer: any;
  private autoCloseTimer: any;
  private element: HTMLElement;

  constructor(props: ViewProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclViewBase;
    }) as NclViewBase<CSNclViewMetadata, UpdateHeadered>;
    this.state = { data: this.control.init(this) as UpdateHeadered, vcxVersion: -1 };
  }

  getOverRect(): DOMRect {
    let result = undefined;

    if (this.state.data.Visible === true) {
      if (this.element) {
        result = this.element.getBoundingClientRect();
      }
    }

    return result;
  }

  componentWillUnmount() {
    this.control.willUnMount(true);
    this.control = null;
    this.stopAutoUpdate();
    this.stopAutoClose();
  }

  componentDidMount() {
    this.startAutoUpdate();
  }

  updateState(state: UpdateControl) {
    this.setState((prevState: K2ComponentState<UpdateHeadered>) => {
      return { data: state as UpdateHeadered };
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  close(): Promise<void> {
    if (this.window) {
      return this.window.close();
    } else {
      return Promise.resolve();
    }
  }

  show(): Promise<void> {
    if (this.window) {
      if (this.control.getAutoCloseTime() > 0) {
        return new Promise((resolve, reject) => {
          this.window
            .show()
            .then(() => {
              resolve();
              this.startAutoClose();
            })
            .catch(reject);
        });
      } else {
        return this.window.show();
      }
    } else {
      return Promise.resolve();
    }
  }

  private startAutoUpdate() {
    if (this.control && this.control.Ncl.FrgtData.AutoUpdateInterval > 0) {
      this.stopAutoUpdate();
      this.timer = setInterval(() => {
        this.control.callAutoUpdate();
      }, Math.max(this.control.Ncl.FrgtData.AutoUpdateInterval, 1000));
    }
  }

  private stopAutoUpdate() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private startAutoClose() {
    if (this.control && this.control.getAutoCloseTime() > 0) {
      this.stopAutoClose();
      this.autoCloseTimer = setInterval(() => {
        if (this.control.getAutoCloseTimeRemaining() <= 0) {
          this.control.callAutoClose();
        }
      }, 1000);
    }
  }

  private stopAutoClose() {
    if (this.autoCloseTimer) {
      clearInterval(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }

  private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.keyCode === 27 && this.control instanceof NclFloaterView && this.control.Parent instanceof NclFloaterAccessor) {
      this.control.Parent.hide();
      e.stopPropagation();
    }
  };

  render() {
    let isMenu: boolean = false;
    let content: JSX.Element = null;
    if (this.control.Content) {
      if (this.control.Content instanceof NclMenu) {
        isMenu = true;
        content = (
          <K2Menu
            style={{ height: "100%" }}
            controlUID={this.control.Content.MetaData.ControlUID}
            vrUID={this.control.Content.getRealizerUID()}
            depth={0}
            subMenuDisplayInside={Context.DeviceInfo.StyleOfModalWindowShow === TBehaviorTypeByDevice.btbdMobile}
          />
        );
      } else if (this.control instanceof NclOpenDialog) {
        content = <K2OpenDialog controlUID={this.control.MetaData.ControlUID} vrUID={this.control.getRealizerUID()} />;
      } else if (this.control.Content instanceof NclSilentOpenDialogContent) {
        content = <K2OpenDialogContent controlUID={this.control.Content.MetaData.ControlUID} vrUID={this.control.Content.getRealizerUID()} />;
      } else if (this.control instanceof NclVirtualKeyboardDialog) {
        content = (
          <K2VirtualKeyboard controlUID={this.control.MetaData.ControlUID} vrUID={this.control.getRealizerUID()} mode={this.control.getAlphabetMode()} />
        );
      } else if (this.control instanceof NclCodeReaderDialog) {
        content = <K2CodeReader controlUID={this.control.MetaData.ControlUID} vrUID={this.control.getRealizerUID()} />;
      } else {
        content = <K2FlowPanel controlUID={this.control.Content.MetaData.ControlUID} vrUID={this.control.Content.getRealizerUID()} />;
      }
    }
    let vr = ViewRealizerManager.getViewRealizer(this.control.getRealizerUID());

    let view = (
      <div
        ref={(ref) => (this.element = ref)}
        data-k2-edit-mode={this.control.InEditMode}
        data-k2-test-id={this.control.getName()}
        style={StyleHelper(
          this.control,
          Object.assign(
            {
              flexDirection: "column",
              flex: "1 1 auto",
              position: "relative",
              alignSelf: "stretch",
              background: this.control.VCX.getColor(this.control.VCX.Data.ColorMap.ContentColorBck1),
            },
            this.props.style
          )
        )}
        onKeyDown={this.handleKeyDown}
      >
        {Context.getApplication().UserInfo.IsTestVersion && vr === Context.getApplication().appViewRealizer && (
          <div className="testPanel">Test Test Test Test</div>
        )}
        {this.props.children}
        {this.control.isShowHeader() && (
          <K2Header
            controlUID={this.control.Header.MetaData.ControlUID}
            vrUID={this.control.getRealizerUID()}
            title={this.state.data.Title}
            className={"handle_" + this.control.MetaData.ControlUID}
          />
        )}
        {content}
      </div>
    );

    if (
      !vr.showAsLocalModal() &&
      !vr.showAsModal() &&
      !(this.control instanceof NclFloaterView) &&
      !(this.control instanceof NclMenuView) &&
      !(this.control instanceof NclOpenDialog)
    )
      return view; // View dock in main docked layout

    let window = null;
    let showType = Context.DeviceInfo.StyleOfModalWindowShow;
    if (this.control instanceof NclInplaceView) {
      showType = Context.DeviceInfo.InplaceEditBehavior;
    }

    switch (showType) {
      case TBehaviorTypeByDevice.btbdMobile: {
        if (this.control instanceof NclMenuView && this.control.isOnlyActionsInFirstLevel(3)) {
          window = (
            <ModalMenuWindow
              isOverlayBck={this.props.overlayBck}
              ref={(ref) => {
                this.window = ref;
              }}
              renderer={this.props.renderer}
              vcx={this.control.VCX}
              realizerUID={vr.getRealizerUID()}
              controlUID={this.control.MetaData.ControlUID}
            >
              {view}
            </ModalMenuWindow>
          );
        } else {
          window = (
            <SimpleModalWindow
              isOverlayBck={this.props.overlayBck}
              ref={(ref) => {
                this.window = ref;
              }}
              renderer={this.props.renderer}
              vcx={this.control.VCX}
              realizerUID={vr.getRealizerUID()}
              controlUID={this.control.MetaData.ControlUID}
            >
              {view}
            </SimpleModalWindow>
          );
        }
        break;
      }
      case TBehaviorTypeByDevice.btbdNormal: {
        if (isMenu) {
          window = (
            <ModalMenuWindow
              isOverlayBck={this.props.overlayBck}
              ref={(ref) => {
                this.window = ref;
              }}
              renderer={this.props.renderer}
              vcx={this.control.VCX}
              realizerUID={vr.getRealizerUID()}
              controlUID={this.control.MetaData.ControlUID}
            >
              {view}
            </ModalMenuWindow>
          );
        } else {
          window = (
            <ModalWindow
              isOverlayBck={this.props.overlayBck}
              ref={(ref) => {
                this.window = ref;
              }}
              renderer={this.props.renderer}
              vcx={this.control.VCX}
              realizerUID={vr.getRealizerUID()}
              controlUID={this.control.MetaData.ControlUID}
            >
              {view}
            </ModalWindow>
          );
        }
        break;
      }
    }

    return window;

    return null;
  }
}

export const K2View = withContext(_View);
